import React, { useEffect, useState } from "react";
import axios from "axios";
import Navbar from "../../components/ui/Navbar";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { LoaderPinwheel } from "lucide-react";
import PageSizeSelect from "../../components/ui/PageSizeSelect";

const WarehouseStockPage = () => {
  const { almacenId } = useParams();
  const navigate = useNavigate();
  const [almacen, setAlmacen] = useState(null);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [itemsPerPage, setItemsPerPage] = useState(10);

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8001/api";

  const fetchStockAlmacen = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/almacenes/${almacenId}/stock`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlmacen(res.data.almacen);
      setStock(res.data.stock);
    } catch (err) {
      toast.error("No se pudo cargar el stock del almacén");
    } finally {
      setLoading(false);
    }
  };

  const updateStockConfig = async (item) => {
    const itemId = item._id;
    try {
      setSaving((prev) => ({ ...prev, [itemId]: true }));
      const payload = {
        codigo_item: item.codigo_item,
        articulo: item.articulo,
        stock_minimo:
          item.stock_minimo === "" || item.stock_minimo === null
            ? null
            : Number(item.stock_minimo),
        stock_maximo:
          item.stock_maximo === "" || item.stock_maximo === null
            ? null
            : Number(item.stock_maximo),
      };

      const res = await axios.put(
        `${API_URL}/almacenes/${almacenId}/stock-config`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setStock((prev) =>
        prev.map((current) =>
          current._id === itemId
            ? {
                ...current,
                stock_minimo: res.data.stock_minimo,
                stock_maximo: res.data.stock_maximo,
              }
            : current,
        ),
      );

      toast.success("Stock mínimo/máximo actualizado");
    } catch (err) {
      toast.error("No se pudo actualizar el stock mínimo/máximo");
    } finally {
      setSaving((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const updateStockField = (itemId, field, value) => {
    setStock((prev) =>
      prev.map((item) =>
        item._id === itemId ? { ...item, [field]: value } : item,
      ),
    );
  };

  useEffect(() => {
    fetchStockAlmacen();
    // eslint-disable-next-line
  }, [almacenId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, stock]);

  const filteredStock = stock.filter((item) => {
    const term = searchTerm.toLowerCase();
    return [
      item.articulo,
      item.codigo_item,
      item.tipo_ultimo_movimiento,
      item.stock_actual,
      item.stock_minimo,
      item.stock_maximo,
    ].some((value) => String(value ?? "").toLowerCase().includes(term));
  });

  const totalPages = Math.max(1, Math.ceil(filteredStock.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedStock = filteredStock.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-gray-600">
        <LoaderPinwheel className="w-10 h-10 mb-3 animate-spin text-blue-600" />
        <p>Cargando almacén...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28">
        <Card className="shadow-md" data-testid="warehouse-stock-page">
          <CardContent>
            <div className="flex justify-between items-center mb-6">
              <div>
                <Button
                  variant="outline"
                  onClick={() => navigate("/storage")}
                  data-testid="back-btn"
                  className="mb-2"
                >
                  ← Volver
                </Button>
                <h2 className="text-2xl font-semibold text-gray-800">
                  📦 Stock de {almacen?.nombre}
                </h2>
                <span
                  className={`inline-block px-3 py-1 text-xs font-semibold rounded-full mt-2 ${
                    almacen?.tipo === "principal"
                      ? "bg-green-100 text-green-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {almacen?.tipo === "principal"
                    ? "Almacén Principal"
                    : "Almacén Secundario"}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por artículo, código o tipo de movimiento"
                className="w-full md:w-96 rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            <div className="flex items-center justify-end mb-2">
              <PageSizeSelect
                value={itemsPerPage}
                onChange={(size) => {
                  setItemsPerPage(size);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-sm">
                <thead className="bg-gray-100 text-gray-700 text-sm uppercase">
                  <tr>
                    <th className="py-3 px-4 text-left">#</th>
                    <th className="py-3 px-4 text-left">Artículo</th>
                    <th className="py-3 px-4 text-left">Código</th>
                    <th className="py-3 px-4 text-right">Stock Actual</th>
                    <th className="py-3 px-4 text-right">Stock Mín.</th>
                    <th className="py-3 px-4 text-right">Stock Máx.</th>
                    <th className="py-3 px-4 text-right">Precio Unit.</th>
                    <th className="py-3 px-4 text-left">Último Mov.</th>
                    <th className="py-3 px-4 text-left">Tipo</th>
                    <th className="py-3 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedStock.length > 0 ? (
                    paginatedStock.map((item, idx) => {
                      const hasMin =
                        item.stock_minimo !== null &&
                        item.stock_minimo !== undefined &&
                        item.stock_minimo !== "";
                      const lowStock = hasMin
                        ? item.stock_actual <= Number(item.stock_minimo)
                        : item.stock_actual < 10;
                      return (
                        <tr
                          key={item._id}
                          className="border-b hover:bg-gray-50"
                          data-testid={`stock-row-${startIndex + idx}`}
                        >
                          <td className="py-3 px-4">{startIndex + idx + 1}</td>
                          <td className="py-3 px-4 font-medium">{item.articulo || "—"}</td>
                          <td className="py-3 px-4">{item.codigo_item || "—"}</td>
                          <td className="py-3 px-4 text-right font-semibold">
                            <span className={lowStock ? "text-red-600" : "text-green-600"}>
                              {item.stock_actual}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <input
                              type="number"
                              min="0"
                              value={item.stock_minimo ?? ""}
                              onChange={(event) =>
                                updateStockField(item._id, "stock_minimo", event.target.value)
                              }
                              className="w-24 border rounded px-2 py-1 text-right"
                              placeholder="—"
                            />
                          </td>
                          <td className="py-3 px-4 text-right">
                            <input
                              type="number"
                              min="0"
                              value={item.stock_maximo ?? ""}
                              onChange={(event) =>
                                updateStockField(item._id, "stock_maximo", event.target.value)
                              }
                              className="w-24 border rounded px-2 py-1 text-right"
                              placeholder="—"
                            />
                          </td>
                          <td className="py-3 px-4 text-right">${item.precio_unitario?.toLocaleString() || 0}</td>
                          <td className="py-3 px-4">
                            {item.fecha_ultimo_movimiento
                              ? new Date(item.fecha_ultimo_movimiento).toLocaleDateString()
                              : "—"}
                          </td>
                          <td
                            className={`py-3 px-4 font-semibold ${
                              item.tipo_ultimo_movimiento === "Ingreso"
                                ? "text-green-600"
                                : item.tipo_ultimo_movimiento === "Transferencia"
                                  ? "text-blue-600"
                                  : "text-red-600"
                            }`}
                          >
                            {item.tipo_ultimo_movimiento || "—"}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateStockConfig(item)}
                              disabled={saving[item._id]}
                            >
                              {saving[item._id] ? "Guardando..." : "Guardar"}
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center py-8 text-gray-500">
                        No hay artículos para la búsqueda actual
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
              <span>
                Mostrando {filteredStock.length === 0 ? 0 : startIndex + 1} - {Math.min(startIndex + itemsPerPage, filteredStock.length)} de {filteredStock.length}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <span>
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Siguiente
                </Button>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-2">Resumen del Almacén</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total de artículos:</span>
                  <span className="font-semibold ml-2">{filteredStock.length}</span>
                </div>
                <div>
                  <span className="text-gray-600">Stock total:</span>
                  <span className="font-semibold ml-2">
                    {filteredStock.reduce((acc, item) => acc + item.stock_actual, 0)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WarehouseStockPage;
